/* eslint-disable max-len */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import toolkitLogo from '/images/toolkit-logo-light.svg'

import { useModal } from '@hooks/app/modal.hook'
import { useMe } from '@hooks/user/me.hook'
import { useProject } from '@hooks/project/project.hook'

import { getFileUrl } from '@utils/getFileUrl'

import { LocalFile, RemoteFile } from '@types'

import { useProjectEmailSettings } from '@hooks/project/project-email-settings.hook'
import { useSetProjectEmailSettings } from '@hooks/project/update-project-email-settings.hook'

import { UploadPicture } from '@components/UploadPicture'
import { Input } from '@components/Input'
import { Modal } from '@components/Modal'
import { Refresh } from '@components/Refresh'

import {
  BodyParagraph,
  Container,
  EmailSpan,
  GreetingsParagraph,
  GreetingsSpan,
  ImgLogo,
  ObjectBanner,
  ObjectBannerLabel,
  ObjectBannerObjectLabel,
  PersonNameSpan,
  Preview,
  PreviewButtonJoinProject,
  PreviewContainer,
  PreviewContent,
  PreviewContentBody,
  PreviewContentFooter,
  PreviewContentTitle,
  PreviewLabel,
  ProjectNameSpan,
} from './edit-email-auto.styled'

export const EditEmailAutoPage = (): JSX.Element => {
  const { hideModal } = useModal()

  const { t } = useTranslation()

  const { projectId } = useParams()
  const { data, refetch, loading, error } = useProjectEmailSettings(projectId || '')
  const { data: dataProject } = useProject(projectId || '')
  const { getMe } = useMe()
  const { mutate } = useSetProjectEmailSettings()

  const me = getMe()

  const [subjectPrefix, setSubjectPrefix] = useState<string>('')
  const [localFile, setLocalFile] = useState<LocalFile>()
  const [remoteFile, setRemoteFile] = useState<RemoteFile>()
  const [fileChanged, setFileChanged] = useState<boolean>(false)

  const isLoading = loading ? 1 : error ? -1 : 0

  const projectEmailSettings = data?.projectEmailSettings

  const loadDocumentPreview = async () => {
    const file = projectEmailSettings?.file

    if (file?.url) {
      const url = await getFileUrl(file.url)

      const remoteFile: RemoteFile = {
        deleted: false,
        id: file.id,
        name: file.name,
        type: file.type,
        extension: file.extension,
        url,
      }

      setRemoteFile(remoteFile)
    }
  }

  useEffect(() => {
    if (projectEmailSettings) {
      setSubjectPrefix(projectEmailSettings?.subjectPrefix || '')
      loadDocumentPreview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectEmailSettings])

  const handleFileChange = (files: (LocalFile | RemoteFile)[]) => {
    const local = files.filter((file): file is LocalFile => Boolean((file as LocalFile).data))
    const remote = files.filter((file): file is RemoteFile => !Boolean((file as LocalFile).data))
    const undeletedRemoteFiles = remote.filter((file) => !file.deleted)

    if (local.length > 0) {
      setLocalFile(local[0])
      setRemoteFile(undefined)
      setFileChanged(true)
    } else if (undeletedRemoteFiles.length > 0) {
      setRemoteFile(undeletedRemoteFiles[0])
      setLocalFile(undefined)
      setFileChanged(false)
    } else {
      setLocalFile(undefined)
      setRemoteFile(undefined)
      setFileChanged(true)
    }
  }

  const file = localFile || remoteFile

  return (
    <Modal
      title={t('modules_project_pages_edit_email_auto_title')}
      onSubmitLabel={t('modules_project_pages_edit_email_auto_button_save')}
      onClose={() => {
        hideModal('update_project_email_settings')
      }}
      onSubmit={() => {
        if (!projectId) return

        const inputData = {
          projectId,
          subjectPrefix,
          fileId: !fileChanged ? projectEmailSettings?.file?.id : undefined,
        }

        const fileToSend = fileChanged ? localFile : undefined

        mutate(inputData, fileToSend)

        hideModal('update_project_email_settings')
      }}
    >
      <Container>
        <Refresh small loading={isLoading} onRefresh={() => refetch(projectId || '')}>
          <Input
            autoFocus
            label={t('modules_project_pages_edit_email_auto_label_header')}
            placeholder={t('modules_project_pages_edit_email_auto_placeholder_header')}
            value={subjectPrefix}
            onChange={(v) => setSubjectPrefix(v)}
            maxLength={100}
          />

          <UploadPicture
            label={t('modules_project_pages_edit_email_auto_label_logo')}
            files={file ? ([file] as (LocalFile | RemoteFile)[]) : []}
            multiple={false}
            setFiles={handleFileChange}
          />

          <Preview>
            <PreviewLabel>{t('modules_project_pages_edit_email_auto_preview_label')}</PreviewLabel>
            <PreviewContainer>
              <ObjectBanner>
                <ObjectBannerObjectLabel>
                  {t('modules_project_pages_edit_email_auto_email_subject_prefix')}
                </ObjectBannerObjectLabel>
                <ObjectBannerLabel>
                  {subjectPrefix ? `${subjectPrefix} - ` : ''}
                  {t('modules_project_pages_edit_email_auto_email_subject_suffix')}
                </ObjectBannerLabel>
              </ObjectBanner>

              <PreviewContent>
                <ImgLogo src={file ? file.url : toolkitLogo} />
                <PreviewContentTitle>{t('modules_project_pages_edit_email_auto_email_title')}</PreviewContentTitle>
                <PreviewContentBody>
                  <GreetingsParagraph>
                    <GreetingsSpan>{t('modules_project_pages_edit_email_auto_email_greeting')}</GreetingsSpan>
                  </GreetingsParagraph>

                  <BodyParagraph>
                    {t('modules_project_pages_edit_email_auto_email_body_part1')}{' '}
                    <PersonNameSpan>
                      {me.firstName} {me.lastName}
                    </PersonNameSpan>{' '}
                    (<EmailSpan>{me.email}</EmailSpan>) {t('modules_project_pages_edit_email_auto_email_body_part2')}{' '}
                    <ProjectNameSpan>{dataProject?.project?.name}</ProjectNameSpan>.
                  </BodyParagraph>
                </PreviewContentBody>

                <PreviewContentFooter>{t('modules_project_pages_edit_email_auto_email_footer')}</PreviewContentFooter>
                <PreviewButtonJoinProject>
                  {t('modules_project_pages_edit_email_auto_button_join')}
                </PreviewButtonJoinProject>
              </PreviewContent>
            </PreviewContainer>
          </Preview>
        </Refresh>
      </Container>
    </Modal>
  )
}
